import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import TouLogo from '../asset/TOULogo'
import TOULogoTitle from '../asset/TOULogoTitle'
import OpenArrow from '../asset/OpenArrow'

interface AccordionSummaryProps {
  expanded: boolean
  onClick: () => void
}

interface AccordionDetailsProps {
  expanded: boolean
}

interface MenuItemProps {
  selected: boolean
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;
  height: 100vh;
  width: 240px;
  padding: 32px 24px 32px 24px;
  border-right: 1px solid #cccccc;
  background: #ffffff;
  gap: 48px;
`

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  width: 192px;
  gap: 8px;
`

const MenuName = styled.div``

const MenuItem = styled.div<MenuItemProps>`
  font-size: 14px;
  font-weight: ${(props) => (props.selected ? '700' : '400')};
  color: ${(props) => (props.selected ? '#333333' : '#666666')};
  cursor: pointer;
`

const StyledLink = styled(RouterLink)`
  text-decoration: none;
  color: inherit;
`

const Accordion = styled.div`
  background-color: #ffffff;
  border: none;
`

const AccordionSummary = styled.div<AccordionSummaryProps>`
  cursor: pointer;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => (props.expanded ? '#F5F5F5' : '#ffffff')};
  color: ${(props) => (props.expanded ? '#2F3C5E' : '#666666')};
  border-radius: 8px;
  font-weight: ${(props) => (props.expanded ? '700' : '600')};

  svg {
    transform: ${(props) => (props.expanded ? 'rotate(-180deg)' : 'rotate(0)')};
    transition: transform 0.5s;
    path {
      fill: ${(props) => (props.expanded ? '#2F3C5E' : '#666666')};
    }
  }
`

const AccordionDetails = styled.div<AccordionDetailsProps>`
  padding: 10px;
  display: ${(props) => (props.expanded ? 'block' : 'none')};
  border-bottom: ${(props) => (props.expanded ? '1px solid #cccccc' : 'none')};
`

const SimpleMenuItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => (props.selected ? '#F5F5F5' : '#ffffff')};
  border-radius: 8px;
  color: ${(props) => (props.selected ? '#2F3C5E' : '#666666')};
  font-weight: ${(props) => (props.selected ? '700' : '600')};
`

const SideMenu = () => {
  const { pathname } = useLocation()
  const [expanded, setExpanded] = useState<string | false>(false)

  const getPanelByPath = (path: string) => {
    if (
      path.includes('/customer-ess') ||
      path.includes('/customer-pv') ||
      path.includes('/customer-load')
    ) {
      return '사업조건설정'
    }

    if (
      path.includes('/electricity-purchase-plans') ||
      path.includes('/electricity-sell-plans')
    ) {
      return 'ESS사업자요금제설계'
    }

    if (path.includes('/economic-pv') || path.includes('/economic-capacity')) {
      return '고객경제성분석'
    }

    if (path.includes('/data-')) {
      return '데이터'
    }

    return false
  }

  useEffect(() => {
    setExpanded(getPanelByPath(pathname))
  }, [pathname])

  const handleAccordionChange = (panel: string) => () => {
    setExpanded(expanded === panel ? false : panel)
  }

  const isSelected = (path: string) => pathname === path

  return (
    <Wrapper>
      <StyledLink to="/home/economic-ess">
        <Logo>
          <TouLogo />
          <TOULogoTitle />
        </Logo>
      </StyledLink>
      <MenuList>
        <StyledLink to="/home/economic-ess">
          <SimpleMenuItem selected={isSelected('/home/economic-ess')}>
            <MenuName>ESS 사업성 분석</MenuName>
          </SimpleMenuItem>
        </StyledLink>
        <Accordion>
          <AccordionSummary
            expanded={expanded === '사업조건설정'}
            onClick={handleAccordionChange('사업조건설정')}
          >
            <MenuName>사업 조건 설정</MenuName>
            <OpenArrow expanded={expanded === '사업조건설정'} />
          </AccordionSummary>
          <AccordionDetails expanded={expanded === '사업조건설정'}>
            <StyledLink to="/home/customer-ess">
              <MenuItem selected={isSelected('/home/customer-ess')}>
                ESS 설비·비용
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/customer-pv">
              <MenuItem selected={isSelected('/home/customer-pv')}>
                PV 발전사업자
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/customer-load">
              <MenuItem selected={isSelected('/home/customer-load')}>
                수용가
              </MenuItem>
            </StyledLink>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expanded={expanded === 'ESS사업자요금제설계'}
            onClick={handleAccordionChange('ESS사업자요금제설계')}
          >
            <MenuName>ESS 사업자 요금제 설계</MenuName>
            <OpenArrow expanded={expanded === 'ESS사업자요금제설계'} />
          </AccordionSummary>
          <AccordionDetails expanded={expanded === 'ESS사업자요금제설계'}>
            <StyledLink to="/home/electricity-purchase-plans">
              <MenuItem
                selected={isSelected('/home/electricity-purchase-plans')}
              >
                전력 구매 요금제 설계
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/electricity-sell-plans">
              <MenuItem selected={isSelected('/home/electricity-sell-plans')}>
                전력 판매 요금제 설계
              </MenuItem>
            </StyledLink>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expanded={expanded === '고객경제성분석'}
            onClick={handleAccordionChange('고객경제성분석')}
          >
            <MenuName>고객 경제성 분석</MenuName>
            <OpenArrow expanded={expanded === '고객경제성분석'} />
          </AccordionSummary>
          <AccordionDetails expanded={expanded === '고객경제성분석'}>
            <StyledLink to="/home/economic-pv">
              <MenuItem selected={isSelected('/home/economic-pv')}>
                PV 발전사업자
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/economic-capacity">
              <MenuItem selected={isSelected('/home/economic-capacity')}>
                수용가
              </MenuItem>
            </StyledLink>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expanded={expanded === '데이터'}
            onClick={handleAccordionChange('데이터')}
          >
            <MenuName>데이터</MenuName>
            <OpenArrow expanded={expanded === '데이터'} />
          </AccordionSummary>
          <AccordionDetails expanded={expanded === '데이터'}>
            <StyledLink to="/home/data-unit">
              <MenuItem selected={isSelected('/home/data-unit')}>
                단가 기본값
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/data-smp">
              <MenuItem selected={isSelected('/home/data-smp')}>SMP</MenuItem>
            </StyledLink>
            <StyledLink to="/home/data-pv">
              <MenuItem selected={isSelected('/home/data-pv')}>
                PV 발전량
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/data-electricity">
              <MenuItem selected={isSelected('/home/data-electricity')}>
                전력 소비량
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/data-kepco-tariffs">
              <MenuItem selected={isSelected('/home/data-kepco-tariffs')}>
                한전 요금제
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/data-holiday">
              <MenuItem selected={isSelected('/home/data-holiday')}>
                공휴일
              </MenuItem>
            </StyledLink>
            <StyledLink to="/home/data-rec">
              <MenuItem selected={isSelected('/home/data-rec')}>
                현물 REC
              </MenuItem>
            </StyledLink>
          </AccordionDetails>
        </Accordion>
      </MenuList>
    </Wrapper>
  )
}

export default SideMenu
